import { useMutation } from "@apollo/client/react";
import { DSModal } from "@components/design-system/organisms";
import {
  UPDATE_AUTO_TEMPLATE,
  CREATE_AUTO_TEMPLATE,
} from "@graphql/mutations/AutoTemplates";
import { usePremiumFeature, useTemplates } from "@hooks";
import { normalizeTemplateTitle } from "@src/utils";
import { useLocation, useNavigate } from "react-router-dom";

import AutoTemplatesList from "./AutoTemplatesList";
import { createNewTemplate } from "./utils";
import { trackOperation } from "../../utils/operationTracking";
import { SCRIBENOTE_DEFAULTS } from "../common/record/constants";

const AutoTemplatesModal = ({
  isOpen,
  onClose,
  onTemplateSelect,
  isReprocessingMode = false,
  hideScribenoteDefaults = false,
}) => {
  const [updateAutoTemplate] = useMutation(UPDATE_AUTO_TEMPLATE);
  const [createAutoTemplate] = useMutation(CREATE_AUTO_TEMPLATE);
  const navigate = useNavigate();
  const { hasFeatureAccess, UserPermissions } = usePremiumFeature();
  const {
    teamAndOrgDefaultTemplates,
    sharedTemplates,
    ownedTemplates,
    refetchTemplates: refetch,
    refetchDefaultTemplates,
  } = useTemplates();
  const location = useLocation();
  const isPimsPal = location.pathname
    .toLowerCase()
    .includes("/pimspal/");

  const refetchAllTemplates = () => {
    refetch();
    refetchDefaultTemplates();
  };

  const handleFavouriteToggle = (template) =>
    trackOperation("template-favorite-toggled", async (ctx) => {
      const newFavouriteState = !template?.isFavourite;

      ctx.addProperties({
        templateUuid: template?.uuid,
        templateOwnerUuid: template?.user?.uuid,
        isFavourited: newFavouriteState,
        source: "template selection modal",
      });

      await updateAutoTemplate({
        variables: {
          input: {
            templateUuid: template?.uuid,
            isFavourite: newFavouriteState,
          },
        },
      });

      refetchAllTemplates();
    });

  // only show owned templates
  const templatesToShow = ownedTemplates;

  const templateLists = [
    {
      title: "SOAPs",
      templates: templatesToShow.filter(
        (t) => t.templateType === "SOAP",
      ),
      templateType: "SOAP",
    },
    {
      title: "Freeform",
      templates: templatesToShow.filter(
        (t) => t.templateType === "CUSTOM",
      ),
      templateType: "CUSTOM",
    },
    ...(teamAndOrgDefaultTemplates.length > 0
      ? [
          {
            title: "Team Defaults",
            templates: teamAndOrgDefaultTemplates,
            templateType: "TEAM_DEFAULTS",
          },
        ]
      : []),
    ...(sharedTemplates.length > 0
      ? [
          {
            title: "Shared with you",
            templates: sharedTemplates,
            templateType: "SHARED",
          },
        ]
      : []),
    ...(!hideScribenoteDefaults
      ? [
          {
            title: "Scribenote Defaults",
            templates: SCRIBENOTE_DEFAULTS,
            templateType: "SCRIBENOTE_DEFAULT",
          },
        ]
      : []),
  ];

  const handleCreateNewTemplate = async () => {
    const templateUuid = await createNewTemplate(
      "SOAP",
      true,
      createAutoTemplate,
    );
    navigate(`/templates/edit/${templateUuid}?isNew=true`);
  };

  const showNewTemplateAction =
    hasFeatureAccess(UserPermissions.ADAPTIVE_TEMPLATES_ENABLED) &&
    !isPimsPal;

  return (
    <DSModal
      isOpen={isOpen}
      onClose={onClose}
      title={
        isReprocessingMode ? "Change Template" : "Select Template"
      }
      subtitle={
        <>
          {isReprocessingMode ? (
            <>
              Select an option below to apply this template to the
              note.
              <div>
                <b className="text-danger">
                  NOTE: This will overwrite any changes you have made
                  to the existing note.
                </b>
              </div>
            </>
          ) : (
            "Select an option below to create a new note using the selected template."
          )}
        </>
      }
      actions={[
        { label: "Cancel", variant: "cancel", onClick: onClose },
        ...(showNewTemplateAction
          ? [
              {
                label: "+ New Adaptive Template",
                variant: "primary",
                onClick: handleCreateNewTemplate,
              },
            ]
          : []),
      ]}
      width="w-full max-w-[1200px]"
      dataTestId="auto-templates-modal"
    >
      <div className="bg-primary rounded-large p-lg pt-0 min-h-96">
        <div className="flex flex-col gap-md px-md">
          {templateLists.map(({ title, templates, templateType }) => (
            <AutoTemplatesList
              dataTestId={`template-section-${normalizeTemplateTitle(
                title,
              )}`}
              key={templateType}
              title={title}
              templates={templates}
              templateType={templateType}
              onFavouriteToggle={handleFavouriteToggle}
              onTemplateClick={(template) => {
                onTemplateSelect(template);
                onClose();
              }}
              isModal={true}
            />
          ))}
        </div>
      </div>
    </DSModal>
  );
};

export default AutoTemplatesModal;
